import cx from 'classnames';
import {
  Heading,
  utils as u,
  Pill,
  enums,
  Icon,
  CopyLink,
  Button,
  Progress,
  Tooltip,
  Loader,
} from 'edrolo-ui';
import { useAnalytics } from 'edrolo/analytics';
import {
  ActivityChunk,
  Attachment,
  Lesson,
} from '../../../../../../types/Assemblage';
import urlResolver from 'edrolo/utils/URLResolver';
import { AttachmentParent } from '../../../../../../enums/Attachments';
import { Link } from 'react-router-dom';
import { useAttachments } from 'edrolo/domains/attachments/hooks';
import { useClassActivityResponses } from 'edrolo/domains/activities/hooks';
import { CardAttachments } from '../CardAttachments';
import { ClassContext, UserContext } from 'edrolo/context';
import { useContext } from 'react';
import s from './styles.scss';

export interface ActivityCardProps {
  activityChunk: ActivityChunk;
  lesson: Lesson;
}

export const ActivityCard = ({ activityChunk, lesson }: ActivityCardProps) => {
  const { profile } = useContext(UserContext);
  const {
    data: { classId, isTeacherView },
  } = useContext(ClassContext);

  const url = urlResolver.reverse('chunkview-chunk', {
    lessonId: lesson.id,
    classId,
    index: activityChunk.index,
  });

  const {
    data: responses,
    loading: responsesLoading,
    error: responsesError,
  } = useClassActivityResponses(activityChunk.activity.id, classId);

  const {
    data: activityAttachmentsData,
    isLoading: attachmentsLoading,
    error,
  } = useAttachments(
    classId,
    AttachmentParent.ACTIVITY,
    activityChunk.activity.id,
    isTeacherView
  );

  const isLoading = attachmentsLoading || responsesLoading;

  const { track: analyticsTrack } = useAnalytics({});

  const track = (eventName: string) =>
    analyticsTrack(eventName, { activity_id: activityChunk.activity.id });

  if (isLoading) {
    return <Loader.Spinner />;
  }

  const attachments: Attachment[] =
    (!isLoading && !error && Object.values(activityAttachmentsData.toJS())) ||
    [];

  const shortLinkProps = {
    id: activityChunk.id,
    appLabel: 'tod',
    model: 'chunk',
    queryString: '?',
  };

  return (
    <div
      className={cx(s.activityCard, {
        [s.highlight]: !lesson.is_published,
      })}
      data-appcues="activity-card"
    >
      <header>
        <div className={s.titleBlock}>
          <Link
            to={url}
            className={cx({
              [s.disabled]: lesson.is_coming_soon && !profile.is_staff,
            })}
            onClick={() => track('activity card clicked')}
          >
            <Pill variant={enums.PillVariant.FADE} color={enums.ColorHue.PINK}>
              <Icon
                name={enums.Icons.ACTIVITY}
                color={enums.ColorHue.PINK}
                className={u.mr1}
              />
              Activity
            </Pill>
          </Link>

          <Link
            to={url}
            className={cx({
              [s.disabled]: lesson.is_coming_soon && !profile.is_staff,
            })}
            onClick={() => track('activity card clicked')}
          >
            <Heading level={4} className={u.bold}>
              {activityChunk.title}
            </Heading>
          </Link>

          {lesson.is_coming_soon ? (
            <Pill color={enums.ColorHue.GREY}>Coming soon</Pill>
          ) : (
            <>
              {activityChunk.activity
                .estimated_time_to_complete_the_activity && (
                <Pill
                  variant={enums.PillVariant.OUTLINE}
                  color={enums.ColorTheme.FOREGROUND}
                >
                  <Icon name={enums.Icons.CLOCK} className={u.mr2} />
                  {
                    activityChunk.activity
                      .estimated_time_to_complete_the_activity
                  }
                </Pill>
              )}
              {activityChunk.activity.skill?.name && (
                <Link
                  to={url}
                  className={cx({
                    [s.disabled]: lesson.is_coming_soon && !profile.is_staff,
                  })}
                  onClick={() => track('activity card clicked')}
                >
                  <Pill
                    variant={enums.PillVariant.OUTLINE}
                    color={enums.ColorTheme.FOREGROUND}
                  >
                    <Icon name={enums.Icons.SKILL} className={u.mr2} />
                    {activityChunk.activity.skill.name}
                  </Pill>
                </Link>
              )}
            </>
          )}
          {!lesson.is_published && (
            <Pill color={enums.ColorHue.GREY}>Unpublished</Pill>
          )}
        </div>

        {!lesson.is_coming_soon && isTeacherView && (
          <div className={cx(u.flex, u.gap2)}>
            <Button.Link
              size={enums.Size.SMALL}
              variant={enums.Variant.GHOST}
              to={url}
              onClick={() => track('activity view responses clicked')}
            >
              <Tooltip
                tip={
                  responsesLoading
                    ? 'Loading student responses...'
                    : responsesError
                    ? 'There was an error loading student responses'
                    : `${responses.responded_profile_ids.length} out of ${
                        responses.responded_profile_ids.length +
                        responses.not_responded_profile_ids.length
                      } students have submitted a response`
                }
              >
                <Progress.Circle
                  numerator={responses.responded_profile_ids.length || 0}
                  denominator={
                    responses.responded_profile_ids.length +
                      responses.not_responded_profile_ids.length || 0
                  }
                  className={u.mr2}
                />
              </Tooltip>
              View responses
            </Button.Link>

            <CopyLink
              buttonProps={{
                size: enums.Size.SMALL,
                variant: enums.Variant.OUTLINE,
              }}
              shortLinkProps={shortLinkProps}
            />
          </div>
        )}
      </header>

      {activityChunk.activity.description && (
        <div className={cx(u.primary6, u.fontSize1, u.mt4)}>
          {activityChunk.activity.description}
        </div>
      )}

      {attachments.length > 0 && (
        <footer>
          <CardAttachments attachments={attachments} />
        </footer>
      )}
    </div>
  );
};
